import customInput48Classes from "@/styles/CustomInput48.module.css";
import { cn } from "@/utils/cn";
import { Combobox, useCombobox } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";

interface CheckoutSelectProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  data: { value: string; label: string }[];
  required?: boolean;
  className?: string;
  showValidation?: boolean;
  customValidation?: (value: string) => boolean;
}

export function CheckoutSelect({
  label,
  name,
  value,
  onChange,
  onBlur,
  data,
  required = false,
  className = "",
  showValidation = false,
  customValidation,
}: CheckoutSelectProps) {
  const combobox = useCombobox({
    onDropdownClose: () => setActiveIndex(-1),
    onDropdownOpen: () => {
      // Pre-highlight the currently selected item when the dropdown opens
      const idx = data.findIndex(item => item.value === value);
      setActiveIndex(idx);
    },
  });

  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const optionsRef = useRef<HTMLDivElement>(null);
  // PIN #7 (2026-07-19): true type-ahead — accumulate typed characters into
  // a short-lived buffer so "a l a s" reaches Alaska instead of re-jumping
  // to Alabama on every keypress; repeating the same letter cycles through
  // its matches (standard combobox behavior).
  const typeBufferRef = useRef("");
  const typeTimerRef = useRef<number | null>(null);

  const isOpen = combobox.dropdownOpened;
  const shouldFloatLabel = isFocused || isOpen || Boolean(value);
  const isInvalid =
    showValidation &&
    required &&
    (!value || (customValidation && !customValidation(value)));

  const selectedLabel = data.find(item => item.value === value)?.label ?? "";

  // Scroll the active option into view whenever activeIndex changes
  useEffect(() => {
    if (!isOpen || activeIndex < 0 || !optionsRef.current) return;
    const options = optionsRef.current.querySelectorAll(
      "[data-combobox-option]"
    );
    const target = options[activeIndex] as HTMLElement | undefined;
    target?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          combobox.openDropdown();
        } else {
          setActiveIndex(i => Math.min(i + 1, data.length - 1));
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, 0));
        break;
      case "Enter":
        if (!isOpen) {
          // PIN #7: Enter on a closed combobox opens it (standard behavior)
          e.preventDefault();
          combobox.openDropdown();
        } else if (activeIndex >= 0) {
          e.preventDefault();
          onChange(data[activeIndex].value);
          combobox.closeDropdown();
        }
        break;
      case "Escape":
        combobox.closeDropdown();
        break;
      case " ":
        if (!isOpen) {
          e.preventDefault();
          combobox.openDropdown();
        }
        break;
      default:
        // PIN #7: type-ahead with a multi-character buffer (700ms reset).
        // "a" jumps to Alabama, "al"+"a"+"s" reaches Alaska; repeating the
        // same letter cycles through that letter's matches.
        if (e.key.length === 1 && /\S/.test(e.key)) {
          e.preventDefault();
          if (!isOpen) combobox.openDropdown();
          const char = e.key.toLowerCase();
          const buffer = typeBufferRef.current + char;
          const isSameLetterRepeat =
            buffer.length > 1 && buffer.split("").every(c => c === buffer[0]);
          const prefix = isSameLetterRepeat ? buffer[0] : buffer;
          const matches = data
            .map((item, index) => ({ item, index }))
            .filter(entry =>
              entry.item.label.toLowerCase().startsWith(prefix)
            );
          if (matches.length > 0) {
            if (isSameLetterRepeat) {
              const next =
                matches.find(entry => entry.index > activeIndex) || matches[0];
              setActiveIndex(next.index);
            } else {
              setActiveIndex(matches[0].index);
            }
            typeBufferRef.current = buffer;
          } else {
            // Dead-end buffer: restart from this character alone
            const fresh = data.findIndex(item =>
              item.label.toLowerCase().startsWith(char)
            );
            if (fresh !== -1) setActiveIndex(fresh);
            typeBufferRef.current = char;
          }
          if (typeTimerRef.current) window.clearTimeout(typeTimerRef.current);
          typeTimerRef.current = window.setTimeout(() => {
            typeBufferRef.current = "";
          }, 700);
        }
    }
  };

  const triggerInputClass = [
    customInput48Classes.input,
    shouldFloatLabel ? customInput48Classes.inputFocused : "",
    isInvalid ? "!border-[#c1332b] !border-2 !bg-[#c1332b08]" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const triggerLabelClass = [
    `${customInput48Classes.label}  top-0`,
    shouldFloatLabel ? `${customInput48Classes.labelFloating} top-0` : "",
    isInvalid ? "!text-[#c1332b]" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Combobox
      store={combobox}
      // PIN #5 (2026-07-19): the checkout's sticky Previous/Save bar sits at
      // z-[500]; Mantine's dropdown default z-index is below that, so the
      // open list rendered UNDERNEATH the footer — Alaska was literally
      // unselectable (Sim 9). Portal the dropdown to <body>, raise it above
      // the footer (but under tooltips at 9999), and let it flip upward when
      // there is no room below.
      withinPortal
      zIndex={600}
      position="bottom"
      middlewares={{ flip: true, shift: true }}
      onOptionSubmit={val => {
        onChange(val);
        combobox.closeDropdown();
      }}
    >
      <input type="hidden" name={name} value={value} />
      <Combobox.Target>
        {/* Mimics Mantine's Input appearance using the same CSS module classes */}
        <div
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-required={required}
          tabIndex={0}
          className={cn(
            "relative flex flex-1 cursor-default select-none items-center rounded border border-[#ced4da] bg-white outline-none focus:border-[#4aa0a0] focus:ring-2 focus:ring-[#4aa0a040]",
            isInvalid && "!border-2 !border-[#c1332b] !bg-[#c1332b08]"
          )}
          style={{ minHeight: 48 }}
          onClick={() => {
            combobox.toggleDropdown();
            setIsFocused(true);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
          onKeyDown={handleKeyDown}
        >
          {/* Floating label */}
          <span className={triggerLabelClass}>
            {label}
            {required && <span className="text-[#fa5252]"> *</span>}
          </span>

          {/* Selected value — only visible once label has floated */}
          <span
            className={triggerInputClass}
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              border: "none",
              boxShadow: "none",
              background: "transparent",
              cursor: "inherit",
              padding: "14px 0px 2px 12px",
              color: shouldFloatLabel ? "#191919" : "transparent",
            }}
          >
            {selectedLabel}
          </span>

          {/* Chevron */}
          <div
            className="bg- pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transition-transform duration-200"
            style={{
              transform: `translateY(-50%) rotate(${isOpen ? 180 : 0}deg)`,
            }}
          >
            <IconChevronDown
              size={16}
              stroke={1.5}
              style={{ color: "#919494" }}
            />
          </div>
        </div>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options
          ref={optionsRef}
          mah={240}
          style={{ overflowY: "auto" }}
        >
          {data.map((item, index) => (
            <Combobox.Option
              key={item.value}
              value={item.value}
              active={item.value === value}
              style={
                index === activeIndex
                  ? { backgroundColor: "var(--mantine-color-gray-1)" }
                  : undefined
              }
            >
              {item.label}
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
